<template>
  <div class="competition-settings">
    <!-- Arriba del todo: Back + Title + Subtitle -->
    <SettingsHeader
      :title="$t('title_TournamentSettings')"
      :description="$t('competitionDescription')"
      @back="goBack"
    />

    <!-- Tabs debajo del header, pero fuera del layout/grid -->
    <SettingsTabs v-model="currentTab" :tabs="tabs" :primary="colors.primary" />

    <!-- Abajo: layout con contenido + preview -->
    <SettingsLayout>
      <template #left>
        <component
          ref="activeTab"
          :is="activeTabComponent"
          :setNextBtnDisabled="setNextBtnDisabled"
          @save-success="handleSaveSuccess"
          @save-error="handleSaveError"
        />
        <div class="left-footer"
             :class="{ 'align-left-card': ['BrandingTab','RankingTab'].includes(activeTabComponent) }"
        >
          <SettingsFooterActions
            :disabled="saveDisabled"
            @cancel="onCancel"
            @save="onSave"
          />
        </div>
      </template>

      <template #right>
        <CompetitionPreviewPanel
          v-if="['BasicsTab', 'PrizesTab', 'RulesTab'].includes(activeTabComponent)"
          :activeTab="activeTabComponent"
        />
      </template>
    </SettingsLayout>

    <!-- Snackbar for messages -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import SettingsHeader from "../Components/SettingsHeader.vue";
import SettingsLayout from "../Components/SettingsLayout.vue";
import SettingsTabs from "../Components/SettingsTabs.vue";
import SettingsFooterActions from "../Components/SettingsFooterActions.vue";
import CompetitionPreviewPanel from "../Components/CompetitionPreviewPanel.vue";
import Repository from "../../../../repositories/tournament";
import { mapMutations } from "vuex";

import BasicsTab from "../Components/BasicsTab.vue";
import PrizesTab from "../Components/PrizesTab.vue";
import RulesTab from "../Components/RulesTab.vue";
import BrandingTab from "../Components/BrandingTab.vue";
import RankingTab from "../Components/RankingTab.vue";
import SkinThemeTab from "../Components/SkinThemeTab.vue";

export default {
  name: "TournamentSettingsView",
  components: {
    SettingsHeader,
    SettingsLayout,
    SettingsTabs,
    SettingsFooterActions,
    CompetitionPreviewPanel,
    BasicsTab,
    PrizesTab,
    RulesTab,
    BrandingTab,
    RankingTab,
    SkinThemeTab,
  },
  props: {
  },
  data() {
    return {
      currentTab: 0,
      colors: { primary: "#28EA96" },
      form: {
        name: "",
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        timezone: "UTC-5",
        defaultLanguage: "en",
        faqLink: "",
      },
      nextBtnDisabled: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  computed: {
    tabs() {
      return [
        { key: 0, label: this.$t("basicsTab"), icon: "fa-regular fa-pen-clip" },
        { key: 1, label: this.$t("prizesTab"), icon: "fa-regular fa-trophy" },
        { key: 2, label: this.$t("rulesTab"), icon: "fa-regular fa-list" },
        {
          key: 3,
          label: this.$t("brandingTab"),
          icon: "fa-regular fa-palette",
        },
        { key: 4, label: this.$t("rankingTab"), icon: "fa-regular fa-medal" },
        {
          key: 5,
          label: this.$t("skinThemeTab"),
          icon: "fa-regular fa-masks-theater",
        },
      ];
    },
    activeTabComponent() {
      return [
        "BasicsTab",
        "PrizesTab",
        "RulesTab",
        "BrandingTab",
        "RankingTab",
        "SkinThemeTab",
      ][this.currentTab];
    },
    saveDisabled() {
      return this.nextBtnDisabled;
    },
  },
  mounted() {
    this.ensureTournamentHydrated();
  },
  methods: {
    ...mapMutations(['setTournament']),
    resolveTournamentId() {
      // Priority: route query → route param → sessionStorage fallback.
      // sessionStorage is populated below when we successfully resolve an id,
      // so a refresh of the Settings URL (which has no :tournamentId path
      // segment) recovers even when query/params are empty.
      const fromRoute = this.$route.query.tournamentId
        || this.$route.params.tournamentId
        || this.$store.state.tournament?.tournamentId;
      if (fromRoute) return fromRoute;
      try {
        return sessionStorage.getItem('tournamentSettings:lastTournamentId') || null;
      } catch (_) {
        return null;
      }
    },
    ensureTournamentHydrated() {
      const routeId = this.resolveTournamentId();
      if (!routeId) return;
      try {
        sessionStorage.setItem('tournamentSettings:lastTournamentId', String(routeId));
      } catch (_) { /* sessionStorage unavailable */ }
      const storedId = this.$store.state.tournament?.tournamentId;
      if (storedId && String(storedId) === String(routeId)) return;
      // On direct navigation / hard refresh the Settings URL doesn't trigger
      // the Overview fetch, so tournamentSummary stays null on every tab.
      // Hydrate the store once so all tabs can read tournamentSettings reliably.
      Repository.getTournamentOverview(routeId).then((response) => {
        if (response.status < 400) {
          this.setTournament(response.data);
        }
      }).catch(() => { /* silent — tabs already guard null tournamentSummary */ });
    },
    setNextBtnDisabled(v) {
      this.nextBtnDisabled = !!v;
    },
    goBack() {
      // Navigate back to tournament-overview
      const tournamentId = this.$store.state.tournament?.tournamentId ||
                          this.$route.params.tournamentId ||
                          this.$route.query.tournamentId;

      if (tournamentId) {
        this.$router.push({
          name: 'tournament-overview',
          query: { tournamentId: tournamentId }
        });
      } else {
        this.$router.back();
      }
    },
    onCancel() {
      this.goBack();
    },
    onSave() {
      // Call the save method of the active tab component
      if (this.$refs.activeTab && typeof this.$refs.activeTab.saveTournamentSettings === 'function') {
        this.$refs.activeTab.saveTournamentSettings();
      } else if (this.$refs.activeTab && typeof this.$refs.activeTab.save === 'function') {
        this.$refs.activeTab.save();
      }
    },
    handleSaveSuccess(event) {
      this.snackbarText = event.message || this.$t('alerts_TournamentSettings.edited');
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    handleSaveError(event) {
      this.snackbarText = event.message || this.$t('alerts.errorOccurred');
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style>
.left-footer {
  width: 100%;
  margin-top: 24px;
  display: flex;
  justify-content: center;
}

/* Para BrandingTab y RankingTab: que el footer tenga el ancho de la columna izquierda del grid interno */
.left-footer.align-left-card {
  /* (100% - gap) * (1.6 / 2.6)  => 0.61538 */
  width: calc((100% - 24px) * 0.61538);
  margin-right: auto;   /* lo pega a la izquierda, debajo de la card izquierda */
  margin-left: 0;
  justify-content: center; /* botones centrados dentro de esa “columna izquierda” */
}
</style>